export interface ListPaginationResult {
  totalItems: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

/**
 * Pulls a nested array from list API payloads (array root or object with known keys).
 */
export function extractListRows(
  data: any,
  nestedKeys: string[]
): {
  rows: any[];
  pagination: Record<string, any> | null;
} {
  if (!data) {
    return { rows: [], pagination: null };
  }

  if (Array.isArray(data)) {
    return { rows: data, pagination: null };
  }

  if (typeof data !== 'object') {
    return { rows: [], pagination: null };
  }

  const obj = data as Record<string, any>;
  let rows: any[] = [];
  for (const key of nestedKeys) {
    const nested = obj[key];
    if (Array.isArray(nested)) {
      rows = nested;
      break;
    }
  }

  const paginationRaw = obj['pagination'];
  const pagination =
    paginationRaw && typeof paginationRaw === 'object' && !Array.isArray(paginationRaw)
      ? (paginationRaw as Record<string, any>)
      : null;

  return { rows, pagination };
}

export function resolveListPagination(
  pagination: Record<string, any> | null,
  defaults: { page: number; limit: number },
  itemCount: number
): ListPaginationResult {
  const totalItems = Number(
    pagination?.['totalItems'] ??
      pagination?.['total'] ??
      pagination?.['total_count'] ??
      pagination?.['total_records'] ??
      itemCount
  );
  const currentPage = Number(
    pagination?.['currentPage'] ??
      pagination?.['page'] ??
      pagination?.['current_page'] ??
      defaults.page
  );
  const itemsPerPage = Number(
    pagination?.['itemsPerPage'] ??
      pagination?.['limit'] ??
      pagination?.['per_page'] ??
      defaults.limit
  );
  const totalPages = Number(
    pagination?.['totalPages'] ??
      pagination?.['total_pages'] ??
      Math.max(1, Math.ceil((totalItems || 0) / (itemsPerPage || defaults.limit || 1)))
  );

  return {
    totalItems: Number.isFinite(totalItems) ? totalItems : itemCount,
    currentPage: Number.isFinite(currentPage) ? currentPage : defaults.page,
    totalPages: Number.isFinite(totalPages) ? totalPages : 0,
    itemsPerPage: Number.isFinite(itemsPerPage) ? itemsPerPage : defaults.limit,
  };
}

/** Top-level `pagination` on admin-panel list responses (outside `data`). */
export function readResponsePagination(res: any): Record<string, any> | null {
  if (!res || typeof res !== 'object') {
    return null;
  }
  const pagination = (res as Record<string, any>)['pagination'];
  if (!pagination || typeof pagination !== 'object' || Array.isArray(pagination)) {
    return null;
  }
  return pagination as Record<string, any>;
}
/**
 * @deprecated Use {@link ListPaginationResult}. Retained so `way-superuser` call sites and
 * models that referenced the old name keep compiling.
 */
export type WaySuperuserListPagination = ListPaginationResult;
